import { presenceNotifyApiAuthError } from "./auth";
import { getPresenceApiBase, readUserId } from "./session";

type ApiErr = { error?: string; message?: string };

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: "invalid_json" };
  }
}

function headers(): HeadersInit {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  const uid = readUserId();
  if (uid) h["X-User-Id"] = uid;
  return h;
}

function throwIfBad(res: Response, data: ApiErr): void {
  if (res.ok) return;
  const err = data.error || "";
  presenceNotifyApiAuthError(res.status, err);
  throw new Error(err || `http_${res.status}`);
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${getPresenceApiBase()}/${path.replace(/^\//, "")}`, {
    credentials: "include",
    cache: "no-store",
    headers: headers(),
  });
  const data = (await parseJson(res)) as T & ApiErr;
  throwIfBad(res, data);
  return data;
}

export async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  const h: Record<string, string> = {};
  const uid = readUserId();
  if (uid) h["X-User-Id"] = uid;
  const res = await fetch(`${getPresenceApiBase()}/${path.replace(/^\//, "")}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: h,
    body: form,
  });
  const data = (await parseJson(res)) as T & ApiErr;
  throwIfBad(res, data);
  return data;
}

export function mediaUrl(path: string): string {
  if (!path) return "";
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  return `${getPresenceApiBase()}/${path.replace(/^\//, "")}`;
}

export async function fetchMediaBlob(path: string): Promise<string> {
  if (!path) return "";
  if (/^(data:|blob:)/i.test(path)) return path;
  const h: Record<string, string> = {};
  const uid = readUserId();
  if (uid) h["X-User-Id"] = uid;
  const res = await fetch(mediaUrl(path), { credentials: "include", cache: "force-cache", headers: h });
  if (!res.ok) throw new Error("bad_media");
  return URL.createObjectURL(await res.blob());
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getPresenceApiBase()}/${path.replace(/^\//, "")}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: headers(),
    body: JSON.stringify(body ?? {}),
  });
  const data = (await parseJson(res)) as T & ApiErr;
  throwIfBad(res, data);
  return data;
}
